import React, { useState, useEffect } from "react";
import BaseModal from "./BaseModal";
import {
  listCalculations,
  getCalculation,
  updateCalculation,
  deleteCalculation,
  type SavedCalculationEntry,
  type CalculationType,
} from "../utils/api";
import "./SavedCalculationsModal.css";

const CALC_LABELS: Record<CalculationType, string> = {
  affordability: "Affordability",
  bond: "Bond Repayment",
  tax: "Tax",
  monthlyFinances: "Monthly Finances",
};

interface SavedCalculationsModalProps {
  open: boolean;
  onClose: () => void;
  onRestoreCalculation: (type: CalculationType, payload: Record<string, unknown>, calculationId: string, name: string, createdAt: string) => void;
}

const SavedCalculationsModal: React.FC<SavedCalculationsModalProps> = ({
  open,
  onClose,
  onRestoreCalculation,
}) => {
  const [list, setList] = useState<SavedCalculationEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setLoading(true);
      listCalculations()
        .then((res) => setList(res.items || []))
        .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleView = async (item: SavedCalculationEntry) => {
    try {
      const full = await getCalculation(item.calculationId);
      const payload = (full.payload || {}) as Record<string, unknown>;
      onRestoreCalculation(item.calculationType, payload, item.calculationId, item.name, item.createdAt);
      onClose();
    } catch {
      setError("Failed to load calculation");
    }
  };

  const startEdit = (item: SavedCalculationEntry) => {
    setEditingId(item.calculationId);
    setEditName(item.name || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const saveEdit = async () => {
    if (!editingId || !editName.trim()) {
      cancelEdit();
      return;
    }
    try {
      await updateCalculation(editingId, { name: editName.trim() });
      setList((prev) =>
        prev.map((i) =>
          i.calculationId === editingId ? { ...i, name: editName.trim() } : i
        )
      );
      cancelEdit();
    } catch {
      setError("Failed to update name");
    }
  };

  const handleDelete = async (item: SavedCalculationEntry) => {
    if (deletingId === item.calculationId) return;
    setDeletingId(item.calculationId);
    try {
      await deleteCalculation(item.calculationId);
      setList((prev) => prev.filter((i) => i.calculationId !== item.calculationId));
    } catch {
      setError("Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const modalContent = (
    <BaseModal onClose={onClose} size="xlarge">
      <div className="saved-modal-header">
        <h2>Saved calculations</h2>
        <button
          type="button"
          className="saved-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="saved-modal-body">
        {loading ? (
          <p className="saved-modal-loading">Loading…</p>
        ) : error ? (
          <p className="saved-modal-error">{error}</p>
        ) : list.length === 0 ? (
          <p className="saved-modal-empty">No saved calculations yet.</p>
        ) : (
          <ul className="saved-modal-list">
            {list.map((item) => (
              <li key={item.calculationId} className="saved-modal-item">
                <div className="saved-modal-item-main">
                  {editingId === item.calculationId ? (
                    <div className="saved-modal-edit-row">
                      <input
                        type="text"
                        className="saved-modal-edit-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit();
                          if (e.key === "Escape") cancelEdit();
                        }}
                        aria-label="Edit name"
                        autoFocus
                      />
                      <button
                        type="button"
                        className="saved-modal-btn saved-modal-btn-primary"
                        onClick={saveEdit}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="saved-modal-btn saved-modal-btn-secondary"
                        onClick={cancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="saved-modal-item-info">
                        <span className="saved-modal-item-name">{item.name}</span>
                        <span className="saved-modal-item-meta">
                          {CALC_LABELS[item.calculationType]} · {formatDate(item.createdAt)}
                        </span>
                      </div>
                      <div className="saved-modal-item-actions">
                        <button
                          type="button"
                          className="saved-modal-btn saved-modal-btn-text"
                          onClick={() => startEdit(item)}
                          title="Edit name"
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="saved-modal-btn saved-modal-btn-primary"
                          onClick={() => handleView(item)}
                          title="Open in calculator"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="saved-modal-btn saved-modal-btn-danger"
                          onClick={() => handleDelete(item)}
                          disabled={deletingId === item.calculationId}
                          title="Delete"
                          aria-label={`Delete ${item.name}`}
                        >
                          {deletingId === item.calculationId ? "…" : "Delete"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </BaseModal>
  );

  if (!open) return null;
  return modalContent;
};

export default SavedCalculationsModal;
